import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  X, 
  Check, 
  Clock, 
  ThumbsUp,
  ThumbsDown,
  FileText,
  Pause
} from 'lucide-react';
import useApi from '../../../hooks/useApi';
import Message from '../../../components/Message';
import Pagination from '../../../components/Pagination';
import { getReceivedInvitations, respondToInvitation } from '../../../services/invitationsService';

const ReceivedInvitationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusFilter = queryParams.get('status');
  
  const [invitations, setInvitations] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  const { 
    loading, 
    error, 
    callApi: fetchInvitations 
  } = useApi(getReceivedInvitations, {showSuccessToast: false});
  
  const { 
    loading: responding, 
    error: responseError, 
    callApi: respondToInvitationApi,
    successMessage,
    setSuccessMessage
  } = useApi(respondToInvitation, {showSuccessToast: true, msg: 'Responded to invitation'});

  const loadInvitations = useCallback(async () => {
    const data = await fetchInvitations(statusFilter, pagination.page, pagination.page_size);
    if (data) {
      setInvitations(data.results);
      setPagination(prev => ({
        ...prev,
        total_pages: data.total_pages,
        count: data.count
      }));
    }
  }, [fetchInvitations, statusFilter, pagination.page, pagination.page_size]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations, statusFilter]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleRespondToInvitation = async (invitationId, action) => {
    try {
      await respondToInvitationApi(invitationId, action);
      setSuccessMessage(`Invitation ${action === 'accept' ? 'accepted' : 'rejected'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reload invitations to update the list
      loadInvitations();
    } catch (err) {
      console.error(`Error ${action}ing invitation:`, err);
    }
  };

  const handleAccessReports = (invId, senderDetails) => {
    navigate(
      `/dashboard/collaborator/${invId}`,
      { state: { inviter: senderDetails } }
    );
  };



  const getStatusBadge = (status) => {
    switch(status) {
      case 'accepted':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Check className="w-3 h-3 mr-1" />Accepted</span>;
      case 'rejected':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><X className="w-3 h-3 mr-1" />Rejected</span>;
      case 'paused':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Pause className="w-3 h-3 mr-1" />Paused</span>;
      default:
        return <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const getPageTitle = () => {
    switch(statusFilter) {
      case 'accepted': return 'Accepted Invitations';
      case 'rejected': return 'Rejected Invitations';
      case 'pending': return 'Pending Invitations';
      case 'paused': return 'Paused Invitations';
      default: return 'Received Invitations';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => navigate('/dashboard/invitations')}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button 
          onClick={() => navigate('/dashboard/invitations/received')}
          className={`px-4 py-2 text-sm font-medium ${!statusFilter ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          All
        </button>
        <button 
          onClick={() => navigate('/dashboard/invitations/received?status=pending')}
          className={`px-4 py-2 text-sm font-medium ${statusFilter === 'pending' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Pending
        </button>
        <button 
          onClick={() => navigate('/dashboard/invitations/received?status=accepted')}
          className={`px-4 py-2 text-sm font-medium ${statusFilter === 'accepted' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Accepted
        </button>
        <button 
          onClick={() => navigate('/dashboard/invitations/received?status=rejected')}
          className={`px-4 py-2 text-sm font-medium ${statusFilter === 'rejected' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Rejected
        </button>
        <button 
          onClick={() => navigate('/dashboard/invitations/received?status=paused')}
          className={`px-4 py-2 text-sm font-medium ${statusFilter === 'paused' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Paused
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <Message type="success" message={successMessage} />
      )}
      {error && (
        <Message type="error" message={error} />
      )}
      {responseError && (
        <Message type="error" message={responseError} />
      )}

      {/* Invitations List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-4 text-center">Loading invitations...</div>
        ) : invitations.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No {statusFilter || ''} invitations found.</p>
            <button
              onClick={() => navigate('/dashboard/invitations')}
              className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
            >
              Back to invitations page
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map(invitation => (
                  <tr key={invitation.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                          {invitation.sender_details?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{invitation.sender_details?.username || 'User'}</p>
                          <p className="text-xs text-gray-500">{invitation.sender_details?.email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {invitation.permission_level === 'view' && 'View'}
                        {invitation.permission_level === 'view_download' && 'View & Download'}
                        {invitation.permission_level === 'full_management' && 'Full Management'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invitation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(invitation.sent_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invitation.status === 'pending' ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleRespondToInvitation(invitation.id, 'accept')}
                            disabled={responding}
                            className="inline-flex items-center px-3 py-1 bg-green-600 rounded-lg text-xs font-medium text-white hover:bg-green-700"
                          >
                            <ThumbsUp className="w-3 h-3 mr-1" />
                            Accept
                          </button>
                          <button
                            onClick={() => handleRespondToInvitation(invitation.id, 'reject')}
                            disabled={responding}
                            className="inline-flex items-center px-3 py-1 bg-red-600 rounded-lg text-xs font-medium text-white hover:bg-red-700"
                          >
                            <ThumbsDown className="w-3 h-3 mr-1" />
                            Reject
                          </button>
                        </div>
                      ) : invitation.status === 'accepted' ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAccessReports(invitation.id, invitation.sender_details)}
                            className="inline-flex items-center px-3 py-1 bg-primary-600 rounded-lg text-xs font-medium text-white hover:bg-primary-700"
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Access Reports
                          </button>
                          
                        </div>
                      ) : (
                        <span className="text-gray-400">No actions available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Invitations"
      />
    </div>
  );
};

export default ReceivedInvitationsPage;